import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { error, failedResponse, getBackendUrl, loaded, loading, LoadState, LoadStateInfo, notStarted } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { PresenceInfo } from "./model";
import { StudentInfo } from "../student/studentSlice";
import { presenceChange } from "./presenceChangeSlice";
import { performPresenceChangeClassroom } from "./presenceChangeClassroomSlice";

export interface StudentPresenceInfo {
    presence?: PresenceInfo;
    student: StudentInfo;
}

export interface PresenceListState {
    state: LoadStateInfo;
    request?: RetrievePresenceListRequest;
    list?: StudentPresenceInfo[];
}

const initialState: PresenceListState = {
    state: notStarted(),
};

export interface RetrievePresenceListRequest {
    subjectPerformedId: string;
}

export const retrievePresenceList = createAsyncThunk(
    "retrievePresenceList",
    async (param: RetrievePresenceListRequest, thunkAPI) => {
        console.debug(`retrieve presence list for subjectPerformed ${param.subjectPerformedId}`);

        const response = await fetch(`${getBackendUrl()}/api/presence/list/${param.subjectPerformedId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (_param: RetrievePresenceListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = (state as any).presenceStudentList?.state as LoadStateInfo | undefined;
            if (status && (status.state == LoadState.Loading || status.state == LoadState.Failed)) {
                return false;
            }
            return true;
        }
    }
);

export const presenceListSlice = createSlice({
    name: "presenceList",
    initialState,
    reducers: {
        check: (state, action: PayloadAction<RetrievePresenceListRequest>) => {
            if (`${state.request?.subjectPerformedId}` != `${action.payload.subjectPerformedId}`) {
                state.state = notStarted();
                state.request = action.payload;
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrievePresenceList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrievePresenceList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => {
                    return a.student.name.localeCompare(b.student.name);
                });
            })
            .addCase(retrievePresenceList.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(presenceChange.fulfilled, (state, action) => {
                if(`${state.request?.subjectPerformedId}` == `${action.meta.arg.subjectPerformedId}`) {
                    console.debug(`${action.type} :: updating check in report`);
                } else {
                    console.debug(`${action.type} :: check change does not match report request, skipping update`);
                    return;
                }
                const studentId = action.meta.arg.studentId;
                if (studentId) {
                    if (!state.list) {
                        state.list = [];
                    }
                    const presence = action.payload as PresenceInfo;
                    const list = state.list;
                    const index = list.findIndex(c => `${c.student.id}` == `${studentId}`);
                    if (index >= 0) {
                        list[index].presence = presence;
                    }
                }
            })
            .addCase(performPresenceChangeClassroom.fulfilled, (state, action) => {
                console.debug(`${action.type} :: presence change rejected, refreshing list if request matches`);
                if(`${state.request?.subjectPerformedId}` == `${action.meta.arg.subjectPerformedId}`) {
                    state.list = action.payload as StudentPresenceInfo[];
                } else {
                    console.debug(`${action.type} :: presence change rejected but does not match report request, skipping update`);
                }
            })
            ;
    },
});

export const selectPresencetList = (state: RootState) => state.presenceList;

export default presenceListSlice.reducer;
